import { useEffect, useRef, useState } from 'react';
import { useAccountStore } from '../stores/account';
import { EDGES } from '../theme/tokens';
import { PLAYLIST, trackUrl, trackLabel } from '../audio/playlist';

// Music player — 15-track playlist served from /cve/songs/. Volume is
// driven by the account store (audioMaster × audioMusic), persisted via
// the store. Auto-play is best-effort (browsers block until user
// gesture); we retry on the first pointerdown/keydown if the initial
// play promise rejects.
//
// On `ended`, advance to the next track. The current index persists to
// the account store so the next session picks up where you left off.

function pickInitialIdx(persistedIdx: number): number {
  if (Number.isInteger(persistedIdx) && persistedIdx >= 0 && persistedIdx < PLAYLIST.length) {
    return persistedIdx;
  }
  // First-time session: random track for variety.
  return Math.floor(Math.random() * PLAYLIST.length);
}

export function MusicPlayer() {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const userPausedRef = useRef(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const audioMaster = useAccountStore((s) => s.audioMaster);
  const audioMusic = useAccountStore((s) => s.audioMusic);
  const setAudioMusic = useAccountStore((s) => s.setAudioMusic);
  const persistedIdx = useAccountStore((s) => s.lastPlayedTrackIdx);
  const setLastPlayedTrackIdx = useAccountStore((s) => s.setLastPlayedTrackIdx);

  const [trackIdx, setTrackIdx] = useState<number>(() => pickInitialIdx(persistedIdx));

  // Persist track index whenever it changes so a refresh resumes here.
  useEffect(() => {
    setLastPlayedTrackIdx(trackIdx);
  }, [trackIdx, setLastPlayedTrackIdx]);

  // Effective volume — 0..1, perceptually eased (squared) so the slider
  // feels linear to the ear.
  const effectiveVolume = (() => {
    const linear = (audioMaster / 100) * (audioMusic / 100);
    return Math.min(1, Math.max(0, linear * linear));
  })();

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    el.volume = effectiveVolume;
  }, [effectiveVolume]);

  // Single mount-time effect: arm the gesture-retry pattern + wire
  // play/pause/ended listeners. We rebind `ended` on track change via
  // its own effect so it picks up the latest setTrackIdx closure.
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;

    const onGesture = () => {
      if (userPausedRef.current) return;
      el.play().catch(() => {});
    };

    el.play().catch(() => {
      window.addEventListener('pointerdown', onGesture, { passive: true });
      window.addEventListener('keydown', onGesture);
    });

    const handlePlay = () => {
      setIsPlaying(true);
      window.removeEventListener('pointerdown', onGesture);
      window.removeEventListener('keydown', onGesture);
    };
    const handlePause = () => setIsPlaying(false);

    el.addEventListener('play', handlePlay);
    el.addEventListener('pause', handlePause);

    return () => {
      window.removeEventListener('pointerdown', onGesture);
      window.removeEventListener('keydown', onGesture);
      el.removeEventListener('play', handlePlay);
      el.removeEventListener('pause', handlePause);
    };
  }, []);

  // Auto-advance — when a track ends, move to the next one and keep playing.
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onEnded = () => {
      setTrackIdx((i) => (i + 1) % PLAYLIST.length);
    };
    el.addEventListener('ended', onEnded);
    return () => el.removeEventListener('ended', onEnded);
  }, []);

  // When trackIdx changes, swap src and resume playing (unless user paused).
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    el.src = trackUrl(trackIdx);
    if (!userPausedRef.current) {
      el.play().catch(() => {});
    }
  }, [trackIdx]);

  function toggle() {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      userPausedRef.current = false;
      el.play().catch(() => {});
    } else {
      userPausedRef.current = true;
      el.pause();
    }
  }

  function prev() {
    setTrackIdx((i) => (i - 1 + PLAYLIST.length) % PLAYLIST.length);
  }
  function next() {
    setTrackIdx((i) => (i + 1) % PLAYLIST.length);
  }

  return (
    <>
      <audio ref={audioRef} preload="metadata" />

      <div className="fixed right-md bottom-[140px] z-30 md:right-lg md:bottom-md">
        {expanded ? (
          <div
            className="flex flex-col gap-xs rounded-md border bg-asphalt/95 px-md py-sm shadow-md backdrop-blur-sm"
            style={{ borderColor: EDGES.mid, minWidth: 220 }}
          >
            <div className="flex items-center justify-between gap-sm">
              <span
                className="truncate font-data text-[10px] uppercase tracking-[0.2em] text-marker"
                title={trackLabel(trackIdx)}
              >
                {trackLabel(trackIdx)}
              </span>
              <button
                onClick={() => setExpanded(false)}
                aria-label="Collapse music widget"
                className="shrink-0 font-data text-[10px] uppercase tracking-[0.2em] text-graphite hover:text-marker"
              >
                ×
              </button>
            </div>
            <div className="flex items-center gap-xs">
              <button
                onClick={prev}
                aria-label="Previous track"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm bg-stucco2 font-data text-[12px] text-bone hover:bg-stucco3"
              >
                ‹‹
              </button>
              <button
                onClick={toggle}
                aria-label={isPlaying ? 'Pause music' : 'Play music'}
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-sm font-data text-[14px] transition-colors ${
                  isPlaying
                    ? 'bg-sodium text-sharpie hover:bg-sodiumGlow'
                    : 'bg-stucco2 text-bone hover:bg-stucco3'
                }`}
              >
                {isPlaying ? '❚❚' : '▶'}
              </button>
              <button
                onClick={next}
                aria-label="Next track"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm bg-stucco2 font-data text-[12px] text-bone hover:bg-stucco3"
              >
                ››
              </button>
              <input
                type="range"
                min={0}
                max={100}
                step={1}
                value={audioMusic}
                onChange={(e) => setAudioMusic(Number(e.target.value))}
                aria-label="Music volume"
                className="h-1 flex-1 cursor-pointer accent-sodium"
              />
            </div>
            <span className="font-data text-[9px] uppercase tracking-[0.2em] text-graphite">
              {trackIdx + 1} / {PLAYLIST.length} · auto-advance
            </span>
          </div>
        ) : (
          <button
            onClick={() => setExpanded(true)}
            aria-label="Open music controls"
            className={`flex h-9 w-9 items-center justify-center rounded-full border font-data text-[14px] shadow-md backdrop-blur-sm transition-colors ${
              isPlaying
                ? 'border-sodium/40 bg-asphalt/80 text-sodium hover:bg-asphalt'
                : 'border-stucco3 bg-asphalt/80 text-graphite hover:text-marker'
            }`}
            style={{ borderColor: isPlaying ? undefined : EDGES.mid }}
          >
            ♪
          </button>
        )}
      </div>
    </>
  );
}
